import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { NotificationEvent } from "@steelbill/types";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import {
  endpoints,
  type NotificationListResponse,
  type NotificationRow,
  type NotificationUnreadCountResponse,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The notification hub's data (spec §17.7, §18.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * POLLING, NOT PUSH — AND THE INTERVAL IS A DECISION, NOT A DEFAULT.
 *
 * There are no websockets in this stack and there should not be: a persistent
 * socket on a phone that spends the day locked in a workshop costs battery and
 * reconnect churn to deliver an event that happens a few times a WEEK. So the
 * bell polls.
 *
 * SIXTY SECONDS. The reasoning, so the next person can disagree with the
 * argument rather than with the number:
 *
 *   WHAT IS BEING WAITED FOR is a human act minutes to hours apart — somebody
 *   saves a bill over ₹1,00,000. Nothing in this system fires faster than a
 *   person can type an invoice, so sub-minute polling cannot deliver anything
 *   sooner than the next thing that happens.
 *
 *   WHAT IT COSTS is one request per minute returning `{"unread":3}` — tens of
 *   bytes, on a metered workshop connection. Sixty requests an hour is invisible
 *   next to a single PDF.
 *
 *   WHAT SLOWER WOULD COST is the whole feature. The window between "the bill is
 *   saved" and "the lorry leaves" is measured in tens of minutes; a five-minute
 *   poll spends a tenth of it doing nothing, for a saving nobody can measure.
 *
 *   AND FOCUS DOES THE REST. `refetchOnWindowFocus` is on globally
 *   (`lib/query-client.ts`), so coming back to the tab is instant regardless of
 *   where in the minute it lands — the interval is the floor, not the mechanism.
 *
 * THE POLL STOPS WHEN THE TAB IS HIDDEN. `refetchIntervalInBackground` is left
 * at its default of `false`, so a phone in a pocket makes no requests at all.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const UNREAD_POLL_MS = 60_000;

/**
 * Whether the hub exists on this server AT ALL — and how the client finds out.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO `useFlag`, AND THIS FEATURE DOES NOT INVENT ONE. `GET /flags`
 * needs `settings.write`, which is ADMIN-only — the OWNER does not hold it, so
 * a client-side flag check would be unanswerable for the very person the bell is
 * for. The `notifications` flag ships OFF, and §18.3 makes a disabled feature a
 * **404, not a 403**, so the 404 IS the answer. Payments and credit notes read
 * their flags exactly this way.
 *
 * A 404 THEREFORE MEANS "SWITCHED OFF", NEVER "NOTHING TO SHOW". The bell
 * renders nothing at all rather than an empty badge — a bell that opens an inbox
 * saying "no notifications" is a promise that alerts would arrive if there were
 * any, which is false while the module is off.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function isModuleOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export interface UnreadCount {
  /** How many are unread. Zero while loading — never a flash of a wrong badge. */
  unread: number;
  /** False when the module is off, nobody is asking, or nothing has ever loaded. */
  available: boolean;
}

/**
 * The badge on the bell.
 *
 * `enabled` IS PASSED IN RATHER THAN READ HERE so the capability check lives in
 * the component beside the thing it hides, and so this hook can be exercised on
 * its own. Called with `false`, it makes no request — a person without
 * `notification.view` must not poll a route that would 403 him once a minute,
 * and every one of those denials is written to `audit_log` by `requireCap`
 * (§2.1). Sixty audited denials an hour is how a security log becomes unreadable.
 */
export function useUnreadCount(enabled: boolean): UnreadCount {
  const query = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () =>
      api.get<NotificationUnreadCountResponse>(endpoints.notificationsUnreadCount),
    enabled,
    refetchInterval: UNREAD_POLL_MS,
    /*
     * The badge is the one number in this app that is worthless when stale — its
     * entire job is to say "something happened since you last looked". The
     * global 60s `staleTime` would let a focus refetch return the cached value,
     * so it is overridden to zero here and nowhere else.
     */
    staleTime: 0,
  });

  /*
   * ───────────────────────────────────────────────────────────────────────────
   * "AVAILABLE" IS ANSWERED BY THE DATA, NOT BY `isSuccess` — AND THAT IS A BUG
   * FIX, NOT A STYLE PREFERENCE.
   *
   * `isSuccess` reads `status === "success"`, and query-core sets
   * `status: "error"` on a FAILED BACKGROUND REFETCH even when `data` is still
   * in the cache (query-core 5.101.4, `query.js` — the "error" case of the
   * reducer keeps `data` and flips `status` regardless). This hook polls once a
   * minute with `staleTime: 0`, so a single dropped poll on workshop wi-fi used
   * to make `available` false — which unmounted the bell, took the whole open
   * `<Drawer>` down with it MID-READ, and threw away the session's dismiss
   * state. The reader lost the panel they were looking at because a counter
   * request failed.
   *
   * So the three states are decided by what we actually know:
   *
   *   NOTHING EVER LOADED   `data === undefined` — no bell yet. Unchanged: the
   *                         chrome does not flash a control before the first
   *                         answer arrives.
   *   MODULE OFF (404)      hidden, whatever is cached. §18.3 — a switched-off
   *                         feature is invisible, and a flag can be turned off
   *                         while somebody has the app open.
   *   ANY OTHER FAILURE     the bell STAYS, showing the last known count. A
   *                         stale count is a smaller wrong than a control that
   *                         vanishes out of the header for up to a minute.
   * ───────────────────────────────────────────────────────────────────────────
   */
  return {
    unread: query.data?.unread ?? 0,
    available: enabled && query.data !== undefined && !isModuleOff(query.error),
  };
}

export interface NotificationListFilters {
  /** "Only what I still have to deal with" — what the hub opens on. */
  unreadOnly?: boolean;
  /**
   * One catalogue key — `invoice.eway_bill.required` and the rest.
   *
   * THE SERVER HAS ALWAYS ACCEPTED THIS (`NotificationListQuerySchema` in
   * `routes/notifications.ts` takes `?event=`); nothing on the client asked for
   * it until the e-way work queue did. The hub itself deliberately does NOT use
   * it — an inbox filtered to one kind of alert is an inbox that hides the
   * others — so this exists for a screen that wants ONE standing question
   * answered, not for a second filter row on the hub.
   *
   * `NotificationEvent` rather than `string`, so a typo is a compile error here
   * rather than a permanently empty list nobody can explain.
   */
  event?: NotificationEvent;
  limit?: number;
  offset?: number;
  /**
   * Defaults to true — the hub always asks.
   *
   * IT IS HERE FOR THE SAME REASON `useUnreadCount` TAKES ONE. A caller without
   * `notification.view` must not make a request the server will refuse: every
   * denial is written to `audit_log` by `requireCap` (§2.1), and a screen that
   * politely retries is how a security log becomes unreadable. The capability
   * check stays in the component beside the thing it hides.
   */
  enabled?: boolean;
}

/**
 * The hub's list.
 *
 * THE ORDER IS THE SERVER'S AND MUST NOT BE RE-SORTED HERE: unread first, then
 * newest first. Sorting these rows by `createdAt` on screen — the obvious thing
 * to do to a list of dated things — would push an unread compliance alert from
 * Tuesday below a read one from this morning, which inverts the whole point of
 * the ordering.
 */
export function useNotifications(
  filters: NotificationListFilters = {},
): UseQueryResult<NotificationListResponse> {
  const { unreadOnly = false, event, limit = 50, offset = 0, enabled = true } = filters;

  return useQuery({
    /*
     * `event` IS PART OF THE KEY. Leaving it out would let the hub's unfiltered
     * list and the e-way queue's filtered one share a cache entry, and whichever
     * mounted second would read the other's rows — the hub showing only e-way
     * alerts, or the queue showing every kind. `QueryFilters` serialises
     * `undefined` away, so the hub's key is unchanged by this addition.
     */
    queryKey: queryKeys.notifications.list({ unreadOnly, event, limit, offset }),
    queryFn: () =>
      api.get<NotificationListResponse>(endpoints.notifications, {
        query: { unreadOnly: unreadOnly ? "true" : undefined, event, limit, offset },
      }),
    enabled,
    staleTime: 0,
  });
}

/**
 * Mark one read — what opening it does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIRE AND FORGET, AND THE NAVIGATION NEVER WAITS FOR IT. The reader tapped a
 * notification to open a bill; blocking that on a round trip so a dot can turn
 * grey would trade the thing they wanted for bookkeeping. If the request fails,
 * the row stays unread — which is the safe direction: an alert shown twice is a
 * nuisance, an alert silently marked read is one nobody ever sees again.
 *
 * IDEMPOTENT SERVER-SIDE. A second call keeps the FIRST timestamp, so a
 * double-tap does not rewrite when she saw it.
 *
 * A 404 IS NOT AN ERROR WORTH SHOWING. It means the row belongs to somebody else
 * — the server answers 404 rather than 403 on purpose (§18.2's reasoning, one
 * level down) — and there is no action the reader could take about it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ data: NotificationRow; marked: number }>(endpoints.notificationRead(id)),
    onSuccess: () => {
      // THE BADGE AND THE LIST TOGETHER. A hub that greys a row while the bell
      // still says 3 is the screen contradicting itself in one viewport.
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

/**
 * Clear the badge in one tap.
 *
 * NOT A DELETE — the rows keep their `read_at`, so "was anybody ever told this
 * bill needed an e-way bill" still has an answer months later. That answer is
 * the reason the table exists, and an inbox that empties itself when cleared
 * cannot give it.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<{ data: { marked: number } }>(endpoints.notificationsReadAll),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}
